import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../hooks/useLanguage';
import Layout from '../components/layout/Layout';
import { authService } from '../services/authService';

const VerifyEmailPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setMessage({
        type: 'error',
        text: 'Invalid verification link. Please check your email for the correct link.'
      });
      return;
    }

    const verifyEmail = async () => {
      setIsLoading(true);
      try {
        const result = await authService.verifyEmail(token);

        setMessage({
          type: 'success',
          text: result.message || 'Email verified successfully!'
        });

        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);

      } catch (error: any) {
        console.error('Email verification failed:', error);
        setMessage({
          type: 'error',
          text: error.message || 'Email verification failed. Please try again.'
        });
      } finally {
        setIsLoading(false);
      }
    };

    verifyEmail();
  }, [token, navigate]);

  return (
    <Layout fullWidth={true}>
      <div className='mt-8'>
        <div className='text-center mb-8'>
          <h1 className='text-3xl font-bold text-gray-900 mb-2'>
            {t('auth.emailVerification')}
          </h1>
          <p className='text-gray-600 text-sm'>
            {t('auth.verifyingEmail')}
          </p>
        </div>

        {isLoading && (
          <div className='text-center'>
            <div className='inline-flex items-center'>
              <svg className='animate-spin -ml-1 mr-3 h-5 w-5 text-blue-500' xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24'>
                <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4'></circle>
                <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'></path>
              </svg>
              <span className='text-sm text-gray-600'>{t('auth.verifying')}</span>
            </div>
          </div>
        )}

        {message && (
          <div className={`p-4 rounded-lg border ${
            message.type === 'success' 
              ? 'bg-green-50 border-green-200 text-green-800' 
              : 'bg-red-50 border-red-200 text-red-800'
          }`}>
            <div className='flex items-center'>
              {message.type === 'success' ? (
                <svg className='h-5 w-5 text-green-400 mr-2' fill='currentColor' viewBox='0 0 20 20'>
                  <path fillRule='evenodd' d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z' clipRule='evenodd' />
                </svg>
              ) : (
                <svg className='h-5 w-5 text-red-400 mr-2' fill='currentColor' viewBox='0 0 20 20'>
                  <path fillRule='evenodd' d='M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z' clipRule='evenodd' />
                </svg>
              )}
              <span className='text-sm font-medium'>{message.text}</span>
            </div>
          </div>
        )}

        <div className='mt-8 text-center'>
          <p className='text-sm text-gray-600'>
            <a href='/login' className='font-semibold text-green-600 hover:text-green-700 transition-colors duration-200'>
              {t('auth.backToLogin')}
            </a>
          </p>
        </div>
      </div>
    </Layout>
  );
};

export default VerifyEmailPage; 